import { useState } from 'react'
import { Eye, EyeOff, Plus, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { apiErrorMessage, teamService, UserOab } from '@/services/team-service'
import * as toast from '@/lib/toast'

interface UserOabsEditorProps {
  userId: string
  oabs: UserOab[]
  canEdit: boolean
  onChanged: () => void
}

/** OABs de um usuário: lista, liga/desliga monitoramento, remove e cadastra (sócio). */
export function UserOabsEditor({ userId, oabs, canEdit, onChanged }: UserOabsEditorProps) {
  const [numero, setNumero] = useState('')
  const [uf, setUf] = useState('')
  const [busy, setBusy] = useState(false)

  const run = async (action: () => Promise<unknown>, okMessage: string) => {
    setBusy(true)
    try {
      await action()
      toast.success(okMessage)
      onChanged()
    } catch (error) {
      toast.error(apiErrorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const add = () =>
    run(async () => {
      await teamService.addOab(userId, { numero, uf, monitorar: true })
      setNumero('')
      setUf('')
    }, 'OAB cadastrada')

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">
        {oabs.length === 0 && <span className="text-xs text-muted-foreground">Sem OAB</span>}
        {oabs.map((oab) => (
          <Badge key={oab.id} variant={oab.monitorar ? 'secondary' : 'outline'} className="gap-1">
            {oab.label}
            {canEdit && (
              <>
                <button
                  type="button"
                  aria-label={oab.monitorar ? `Parar de monitorar ${oab.label}` : `Monitorar ${oab.label}`}
                  title={oab.monitorar ? 'Monitorada (clique para parar)' : 'Não monitorada (clique para monitorar)'}
                  disabled={busy}
                  onClick={() => run(() => teamService.setOabMonitorar(oab.id, !oab.monitorar), 'Monitoramento atualizado')}
                >
                  {oab.monitorar ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
                </button>
                <button
                  type="button"
                  aria-label={`Remover ${oab.label}`}
                  disabled={busy}
                  onClick={() => run(() => teamService.removeOab(oab.id), 'OAB removida')}
                >
                  <X className="size-3" />
                </button>
              </>
            )}
          </Badge>
        ))}
      </div>
      {canEdit && (
        <div className="flex items-center gap-1.5">
          <Input
            aria-label="Número da OAB"
            placeholder="Número"
            inputMode="numeric"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            className="h-7 w-24"
          />
          <Input
            aria-label="UF da OAB"
            placeholder="UF"
            maxLength={2}
            value={uf}
            onChange={(e) => setUf(e.target.value.toUpperCase())}
            className="h-7 w-14"
          />
          <Button size="sm" variant="outline" disabled={busy || !numero || uf.length !== 2} onClick={add}>
            <Plus /> OAB
          </Button>
        </div>
      )}
    </div>
  )
}
